import { Link } from 'react-router-dom';
import { ArrowRight, Lock } from 'lucide-react';
import {
  PLAN_MARKETING_COPY,
  type SubscriptionTier,
  TierFeatureKey,
  hasRole,
} from '@court-sync/shared';
import {
  DashboardEmptyStateCard,
  DashboardEmptyStateIcon,
} from '@/components/dashboard/DashboardEmptyState';
import { FeatureMock, type FeatureMockId } from '@/components/feature-mocks';
import { buttonVariants } from '@/components/ui';
import { Skeleton } from '@/components/ui/Skeleton';
import { cn } from '@/lib/utils';
import { useManagementDashboardFeatureAccess } from '@/hooks/useFeatureAccess';
import { useAuthStore } from '@/store/auth';

const SETTINGS_SUBSCRIPTION_HREF = '/settings?tab=subscription';

function tierDisplayName(tier: SubscriptionTier): string {
  return PLAN_MARKETING_COPY[tier].name;
}

export interface FeatureGateProps {
  feature: TierFeatureKey;
  children: React.ReactNode;
  /** Optional section wrapper class */
  className?: string;
  /** Extra explanation under the title */
  description?: string;
  /** Compact banner below dimmed content instead of a full-width empty-state card */
  inline?: boolean;
  /** When `inline` and access is denied, rendered above dimmed children (section label). */
  inlineLockedHeading?: string;
  /** Animated feature mock shown instead of the lock icon in full-card mode. */
  mock?: FeatureMockId;
}

/**
 * When the user lacks the required plan tier, shows an upgrade CTA instead of hiding the feature.
 * Default mode renders a dashed empty-state card in page flow (same pattern as list empty states).
 * When they have access, renders children unchanged.
 */
export function FeatureGate({
  feature,
  children,
  className,
  description,
  inline = false,
  inlineLockedHeading,
  mock,
}: FeatureGateProps) {
  const { user } = useAuthStore();
  const isDelegatedAdminOnly = !!(user && hasRole(user, 'academy_admin') && !hasRole(user, 'academy_owner'));
  const {
    hasAccess,
    hasAccessFromAcademyDb,
    hasAccessFromOwnerSubscription,
    tierResolved,
    requiredTier,
  } = useManagementDashboardFeatureAccess(feature);

  const hasEarlyAccess = hasAccessFromAcademyDb || hasAccessFromOwnerSubscription;

  /** Avoid false "upgrade" flash while Stripe tier is loading if DB tier already grants access. */
  if (!tierResolved && !hasEarlyAccess) {
    if (inline) {
      return (
        <div className={cn('relative', className)} aria-busy="true" aria-label="Loading plan">
          <Skeleton className="h-24 w-full rounded-lg" />
        </div>
      );
    }
    return (
      <div className={cn('w-full', className)} aria-busy="true" aria-label="Loading plan">
        <Skeleton className="h-48 w-full max-w-md mx-auto rounded-lg" />
      </div>
    );
  }
  if (!tierResolved && hasEarlyAccess) {
    return <>{children}</>;
  }

  if (hasAccess) {
    return <>{children}</>;
  }

  const planName = tierDisplayName(requiredTier);
  const showOwnerUpgradeLink = !isDelegatedAdminOnly;

  if (inline) {
    return (
      <div className={cn('relative', className)}>
        {inlineLockedHeading ? (
          <h2 className="mb-3 text-base font-semibold text-gray-900 dark:text-white">
            {inlineLockedHeading}
          </h2>
        ) : null}
        <div className="pointer-events-none opacity-[0.35] saturate-50">{children}</div>
        <div className="mt-3 flex flex-col gap-3 rounded-lg border border-amber-200/90 bg-amber-50/95 p-4 shadow-sm dark:border-amber-800/80 dark:bg-amber-950/50 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex gap-3">
            <Lock className="mt-0.5 h-5 w-5 shrink-0 text-amber-700 dark:text-amber-400" aria-hidden />
            <p className="text-sm text-amber-950 dark:text-amber-100">
              <span className="font-semibold">{planName}</span> plan required.
              {description ? ` ${description}` : null}
              {isDelegatedAdminOnly
                ? ' The academy owner can upgrade the plan to unlock this feature.'
                : ' Upgrade to unlock this feature.'}
            </p>
          </div>
          {showOwnerUpgradeLink ? (
            <Link
              to={SETTINGS_SUBSCRIPTION_HREF}
              className={cn(
                buttonVariants({ variant: 'default', size: 'sm' }),
                'shrink-0 self-start sm:self-center text-center'
              )}
            >
              Upgrade
            </Link>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('w-full', className)} role="region" aria-labelledby="feature-gate-title">
      <DashboardEmptyStateCard>
        {mock ? (
          <FeatureMock id={mock} size="compact" className="mx-auto" />
        ) : (
          <DashboardEmptyStateIcon icon={Lock} />
        )}
        <p className="text-xs font-semibold uppercase tracking-wider text-violet-600 dark:text-violet-400 mb-1">
          {planName} plan
        </p>
        <h3
          id="feature-gate-title"
          className="text-lg font-semibold text-gray-900 dark:text-white mb-2"
        >
          Unlock this feature
        </h3>
        <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto">
          {description ? (
            <>
              {description}{' '}
            </>
          ) : null}
          {isDelegatedAdminOnly
            ? 'This feature requires a higher plan. Ask your academy owner to upgrade the CourtSync subscription.'
            : 'Upgrade your subscription to use this feature.'}
        </p>
        {showOwnerUpgradeLink ? (
          <Link
            to={SETTINGS_SUBSCRIPTION_HREF}
            className={cn(
              buttonVariants({ variant: 'default', size: 'lg' }),
              'inline-flex items-center justify-center gap-2'
            )}
          >
            Upgrade now
            <ArrowRight className="h-4 w-4 shrink-0" aria-hidden />
          </Link>
        ) : null}
      </DashboardEmptyStateCard>
    </div>
  );
}
